import React, { useState, useEffect } from 'react';
import { Icon } from '@ohif/ui';
import { updateAIData } from '@custom/Apis/apis';
import { servicesManager } from '../../../../../platform/app/src/App';
import { useSelector } from 'react-redux';
import { RootState } from '@custom/store/store';

// Define the structure of bone X-ray data
interface BoneXrayData {
  fractured: boolean;
  probability: string;
}

interface EditableProbabilityFieldProps {
  value: string;
  onChange: (value: string) => void;
  isEditing: boolean;
  className?: string;
}

const EditableProbabilityField = ({ value, className = '' }: EditableProbabilityFieldProps) => {
  // Always render as read-only span, regardless of isEditing
  return <span className={`${className} inline-block w-16 text-right text-yellow-400 font-bold`}>{value}</span>;
};

interface EditableBooleanFieldProps {
  value: boolean;
  onChange: (value: boolean) => void;
  isEditing: boolean;
  className?: string;
}

const EditableBooleanField = ({ value, onChange, isEditing, className = '' }: EditableBooleanFieldProps) => {
  if (isEditing) {
    return (
      <select
        value={value.toString()}
        onChange={(e) => onChange(e.target.value === 'true')}
        className={`bg-gray-700 m-0 p-1 text-white border border-blue-400 rounded px-1 w-20 ${className}`}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    );
  }
  return (
    <span className={`${className} inline-block w-16 text-right ${value ? 'text-red-400' : 'text-green-400'}`}>
      {value ? 'Yes' : 'No'}
    </span>
  );
};


interface BoneXrayModalProps {
  onClose: () => void;
  sopInstanceUID: string;
  seriesInstanceUID: string | null;
  aiData: Record<string, any> | null;
}

const BoneXrayModal = ({ onClose, sopInstanceUID, seriesInstanceUID, aiData }: BoneXrayModalProps): JSX.Element => {
  const { uiNotificationService } = servicesManager.services;
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<{ fractured: boolean; probability: string }>({ fractured: false, probability: '0.00%' });
  const [currentData, setCurrentData] = useState<BoneXrayData | null>(null);
  const userDetail = useSelector((state: RootState) => state?.getUserDetails);

  // Get the bone X-ray data for the current series and SOP
  const seriesData = aiData?.[seriesInstanceUID] || {};
  const boneXrayData: Record<string, BoneXrayData> = seriesData[sopInstanceUID]
    ? { [sopInstanceUID]: seriesData[sopInstanceUID] }
    : {};

  useEffect(() => {
    setDataLoaded(false);

    if (sopInstanceUID && boneXrayData && boneXrayData[sopInstanceUID]) {
      setCurrentData(boneXrayData[sopInstanceUID]);
    } else {
      setCurrentData(null);
    }
    setDataLoaded(true);
  }, [sopInstanceUID, boneXrayData]);

  // Initialize edited data when currentData changes
  useEffect(() => {
    if (currentData) {
      setEditedData({
        fractured: currentData.fractured || false,
        probability: currentData.probability || '0.00%'
      });
    }
  }, [currentData]);

  const handleUpdate = async () => {
    try {
      const hospitalId = localStorage.getItem('hospitalId') || sessionStorage.getItem('hospitalId');
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const studyInstanceUID = url.searchParams.get("StudyInstanceUIDs");

      if (!hospitalId || !studyInstanceUID) {
        throw new Error('Missing required parameters');
      }

      // Prepare data for saving
      const cleanData = {
        fractured: editedData.fractured,
        probability: editedData.probability.includes('%') ? editedData.probability : `${editedData.probability}%`
      };
      const isBoneXray = true;
      await updateAIData(hospitalId, studyInstanceUID, seriesInstanceUID, sopInstanceUID, cleanData, isBoneXray);

      uiNotificationService.show({
        title: 'Success',
        message: 'Bone X-ray AI data updated successfully!',
        type: 'success',
        position: "topRight"
      });

      setIsEditing(false);
    } catch (error) {
      console.error('Error updating bone X-ray AI data:', error);
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to update bone X-ray AI data. Please try again.',
        type: 'error',
        position: "topRight"
      });
    }
  };

  const handleProbabilityChange = (value: string) => {
    setEditedData(prev => ({
      ...prev,
      probability: value
    }));
  };

  const handleFracturedChange = (value: boolean) => {
    setEditedData(prev => ({
      ...prev,
      fractured: value
    }));
  };

  // Convert bone X-ray data to display format
  const displayData = currentData ? {
    fractured: editedData.fractured,
    probability: editedData.probability
  } : null;

  // Loading state
  if (!dataLoaded) {
    return (
      <div className="bg-gray-900 text-white rounded-lg p-4 shadow-xl" style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #4a90e2',
        zIndex: 9999,
        width: '400px',
      }}>
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-blue-500">
          <h3 className="text-blue-400 font-semibold">Loading...</h3>
          <button onClick={onClose} className="text-white hover:bg-gray-700 p-1 rounded">
            ×
          </button>
        </div>
        <div className="p-4 text-center">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderActionButtons = () => (
    <div className="flex justify-end items-center mt-4 pt-3 border-t border-gray-700 text-xs">
      <div className="flex gap-2">
        {isEditing ? (
          <>
            <button
              onClick={() => {
                setEditedData({
                  fractured: currentData?.fractured || false,
                  probability: currentData?.probability || '0.00%'
                });
                setIsEditing(false);
              }}
              className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdate}
              className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
            >
              Update
            </button>
          </>
        ) : (
          userDetail?.result.aiTraining &&
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
          >
            Edit
          </button>
        )}
        <button
          onClick={onClose}
          className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
        >
          Close
        </button>
      </div>
    </div>
  );

  // No data state
  if (!currentData) {
    return (
      <div className="bg-gray-900 text-white rounded-lg p-4 shadow-xl" style={{
        position: 'fixed',
        top: '-200px',
        left: '-200px',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #4a90e2',
        zIndex: 9999,
        width: '400px',
      }}>
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-blue-500">
          <h3 className="text-blue-400 font-semibold">Bone X-ray Analysis Results</h3>
          <button onClick={onClose} className="text-white hover:bg-gray-700 p-1 rounded">
            ×
          </button>
        </div>
        <div className="p-4 text-center text-red-400">
          <p className="text-lg font-medium mb-2">No Data Available</p>
          <p className="text-sm text-gray-400">No bone X-ray analysis data found for the selected SOP Instance.</p>
          <div className="text-sm text-gray-500 mb-2">
            <div>SOP Instance UID: {sopInstanceUID}</div>
            {seriesInstanceUID && (
              <div className="mt-1">Series Instance UID: {seriesInstanceUID}</div>
            )}
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button
            onClick={onClose}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gray-900 text-white rounded-lg p-4 shadow-xl"
      style={{
        position: 'fixed',
        top: '-200px',
        left: '-200px',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #4a90e2',
        borderRadius: '4px',
        padding: '8px',
        color: 'white',
        zIndex: 9999,
        width: '400px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        fontFamily: 'monospace',
        fontSize: '12px',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-blue-500">
        <h3 className="text-blue-400 font-semibold">Bone X-ray Analysis Results</h3>
        <button
          onClick={onClose}
          className="text-white hover:bg-gray-700 p-1 rounded"
        >
          ×
        </button>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
        {displayData ? (
          <div className="border border-gray-700 rounded p-3 bg-gray-800">
            <div className="text-xs font-medium text-blue-400 break-all mb-2">
              SeriesInstanceUID: {seriesInstanceUID}
            </div>
            <div className="ml-2 border-t border-gray-700 pt-2 mt-2">
              <div className="text-xs font-medium text-gray-400 break-all mb-1">
                SOPInstanceUID: {sopInstanceUID}
              </div>
              <div className="space-y-3 mt-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Fractured:</span>
                  <EditableBooleanField
                    value={displayData.fractured}
                    onChange={handleFracturedChange}
                    isEditing={isEditing}
                    className="font-medium"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-400">Probability:</span>
                  <EditableProbabilityField
                    value={displayData.probability}
                    onChange={handleProbabilityChange}
                    isEditing={isEditing}
                    className="text-right text-yellow-400 font-bold"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">No data available</div>
        )}
      </div>

      {renderActionButtons()}
    </div>
  );
};

export default BoneXrayModal;
